import React, { useState, useRef } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { toast, Toaster } from "sonner";
import {
    ArrowUpRight,
    ArrowLeft,
    CheckCircle2,
    MessageCircle,
    Calendar,
    Plus,
    Minus,
    Sparkles,
} from "lucide-react";
import { SERVICES_CONFIG } from "../lib/servicesConfig";
import { MaskedLines, FadeUp } from "../components/motion/Reveal";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { getUtms } from "../lib/utm";
import useSeo from "../hooks/useSeo";
import { trackEvent } from "../lib/track";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_URL =
    "https://wa.me/558581220308?text=Ol%C3%A1%20GreenDuck!%20Vim%20pela%20landing%20de%20servi%C3%A7o.";
const CALENDLY_URL = "https://calendly.com/contatogreenduck/30min";

const SEO_BY_SLUG = {
    apps: {
        title: "Desenvolvimento de Apps Mobile · GreenDuck",
        description:
            "Desenvolvemos apps iOS e Android com performance de flagship. MVP em 60 dias, React Native ou nativo, publicação nas lojas. Solicite proposta.",
    },
    sites: {
        title: "Criação de Sites e Landing Pages de Alta Conversão · GreenDuck",
        description:
            "Sites institucionais, landing pages e e-commerce headless. Design premium, SEO técnico e Core Web Vitals verdes. Solicite proposta.",
    },
    ia: {
        title: "Inteligência Artificial e Chatbots para Empresas · GreenDuck",
        description:
            "Agentes com IA, copilots internos e chatbots com GPT, Claude e Gemini. Automatize atendimento e devolva horas ao seu time. Solicite proposta.",
    },
    automacao: {
        title: "Automação de Processos e Integrações · GreenDuck",
        description:
            "Automatize workflows, integre ERPs, CRMs e planilhas. Elimine 40h/mês de trabalho manual. RPA, n8n, Zapier e APIs sob medida. Solicite proposta.",
    },
};

export default function ServiceLanding() {
    const { slug } = useParams();
    const cfg = SERVICES_CONFIG[slug];
    const seo = SEO_BY_SLUG[slug];
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

    useSeo({
        title: seo?.title || "GreenDuck",
        description: seo?.description || "",
        canonical: `https://www.greenduck.com.br/servicos/${slug}`,
    });

    if (!cfg) return <Navigate to="/" replace />;

    return (
        <div className="App min-h-screen" data-testid={`service-landing-${cfg.slug}`}>
            <Toaster
                position="top-center"
                theme="dark"
                toastOptions={{
                    style: {
                        background: "#0a0a0a",
                        border: "1px solid rgba(255,255,255,0.1)",
                        color: "#fff",
                    },
                }}
            />
            <Navbar />

            {/* HERO */}
            <section
                ref={heroRef}
                className="relative min-h-[92vh] w-full overflow-hidden hero-aura pt-[120px]"
            >
                <div className="absolute inset-0 gd-gridlines opacity-30 pointer-events-none" />
                <motion.div
                    style={{ y: imgY }}
                    className="absolute top-[10%] right-[-8%] w-[55%] h-[75%] hidden lg:block clip-frame overflow-hidden opacity-60"
                >
                    <img src={cfg.heroImg} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-transparent" />
                </motion.div>

                <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 pt-8 pb-24">
                    <FadeUp delay={0}>
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 text-white/50 hover:text-[#00FF66] transition-colors mb-10 text-sm font-mono-tech tracking-widest uppercase"
                            data-testid="back-home"
                        >
                            <ArrowLeft className="w-4 h-4" /> Voltar
                        </Link>
                    </FadeUp>

                    <FadeUp delay={0.05}>
                        <div className="flex items-center gap-3 mb-6">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#00FF66]" />
                            <span className="font-mono-tech text-xs tracking-widest text-[#00FF66] uppercase">
                                {cfg.eyebrow}
                            </span>
                        </div>
                    </FadeUp>

                    <MaskedLines
                        as="h1"
                        lines={cfg.h1.map((l, i) => (
                            <span
                                key={i}
                                className={
                                    i === cfg.h1Accent ? "text-[#00FF66]" : "text-white"
                                }
                            >
                                {l}
                            </span>
                        ))}
                        className="font-display uppercase text-[13vw] md:text-[8vw] font-medium leading-[0.9] tracking-tighter"
                        delay={0.2}
                        stagger={0.14}
                    />

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.9 }}
                        className="mt-10 text-white/70 text-lg md:text-xl leading-relaxed max-w-2xl"
                    >
                        {cfg.subheadline}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.05, duration: 0.9 }}
                        className="mt-10 flex flex-wrap gap-4"
                    >
                        <a href="#briefing" className="btn-neon" data-testid="hero-cta-briefing">
                            <Sparkles className="w-4 h-4" />
                            Solicitar proposta
                        </a>
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() =>
                                trackEvent("contact_whatsapp", {
                                    location: "landing_hero",
                                    service: cfg.eyebrow,
                                })
                            }
                            className="btn-ghost"
                            data-testid="hero-cta-whatsapp"
                        >
                            <MessageCircle className="w-4 h-4" />
                            Falar no WhatsApp
                        </a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.2, duration: 0.9 }}
                        className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 border-t border-white/10 pt-10 max-w-4xl"
                    >
                        {cfg.proofs.map((p) => (
                            <div key={p.k}>
                                <div className="font-display text-4xl md:text-5xl text-white tracking-tight">
                                    {p.k}
                                </div>
                                <div className="mt-2 font-mono-tech text-xs text-white/50 uppercase tracking-widest">
                                    {p.v}
                                </div>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* BENEFITS */}
            <section className="py-32 border-y border-white/10">
                <div className="max-w-[1600px] mx-auto px-6 md:px-10">
                    <FadeUp>
                        <span className="font-mono-tech text-xs tracking-widest text-[#00FF66] uppercase">
                            (Por que a GreenDuck)
                        </span>
                        <h2 className="mt-4 font-display uppercase text-5xl md:text-7xl leading-[0.95] tracking-tighter text-white max-w-4xl">
                            Bem feito.<br />
                            <span className="text-white/40">Bem entregue.</span>
                        </h2>
                    </FadeUp>
                    <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-white/10">
                        {cfg.benefits.map((b, i) => (
                            <FadeUp key={b.title} delay={i * 0.06}>
                                <div
                                    className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-white/10 last:border-r-0 [&:nth-child(-n+2)]:md:border-b hover:bg-white/[0.02] transition-colors group h-full"
                                    data-testid={`benefit-${i}`}
                                >
                                    <div className="flex items-center gap-4 mb-6">
                                        <span className="font-mono-tech text-[#00FF66] text-xs">
                                            0{i + 1}
                                        </span>
                                        <span className="w-8 h-[1px] bg-[#00FF66]/50" />
                                    </div>
                                    <h3 className="font-display uppercase text-2xl md:text-3xl text-white tracking-tight mb-4 group-hover:text-[#00FF66] transition-colors">
                                        {b.title}
                                    </h3>
                                    <p className="text-white/60 leading-relaxed">
                                        {b.body}
                                    </p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* USE CASES */}
            <section className="py-32 border-b border-white/10">
                <div className="max-w-[1600px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-12 gap-6 mb-16">
                        <FadeUp className="col-span-12 md:col-span-6">
                            <span className="font-mono-tech text-xs tracking-widest text-[#00FF66] uppercase">
                                (Aplicações reais)
                            </span>
                            <h2 className="mt-4 font-display uppercase text-5xl md:text-7xl leading-[0.95] tracking-tighter text-white">
                                Onde <span className="text-white/40">isso</span><br />
                                <span className="text-white/40">se aplica.</span>
                            </h2>
                        </FadeUp>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-white/10">
                        {cfg.useCases.map((u, i) => (
                            <FadeUp key={u} delay={i * 0.04}>
                                <div
                                    className="border-r border-b border-white/10 p-8 min-h-[160px] flex items-end gap-4 hover:bg-[#00FF66]/[0.03] transition-colors group"
                                    data-testid={`usecase-${i}`}
                                >
                                    <span className="font-mono-tech text-[#00FF66]/50 group-hover:text-[#00FF66] text-xs transition-colors">
                                        0{i + 1}
                                    </span>
                                    <span className="font-display uppercase text-xl md:text-2xl text-white tracking-tight">
                                        {u}
                                    </span>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <FaqSection faq={cfg.faq} />

            {/* BRIEFING FORM */}
            <BriefingForm service={cfg.eyebrow} />

            <Footer />
        </div>
    );
}

function FaqSection({ faq }) {
    const [open, setOpen] = useState(0);
    return (
        <section className="py-32 border-b border-white/10">
            <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-10">
                <FadeUp className="col-span-12 md:col-span-4">
                    <span className="font-mono-tech text-xs tracking-widest text-[#00FF66] uppercase">
                        (Perguntas)
                    </span>
                    <h2 className="mt-4 font-display uppercase text-5xl md:text-6xl leading-[0.95] tracking-tighter text-white">
                        Dúvidas<br />
                        <span className="text-white/40">frequentes.</span>
                    </h2>
                    <p className="mt-6 text-white/60 leading-relaxed">
                        Não encontrou o que procurava? Fale com a gente no
                        WhatsApp — respondemos em minutos.
                    </p>
                </FadeUp>
                <div className="col-span-12 md:col-span-7 md:col-start-6 border-t border-white/10">
                    {faq.map((item, i) => (
                        <FadeUp key={i} delay={i * 0.04}>
                            <div
                                className="border-b border-white/10 py-6 cursor-pointer group"
                                onClick={() => setOpen(open === i ? -1 : i)}
                                data-testid={`faq-item-${i}`}
                            >
                                <div className="flex items-center justify-between gap-4">
                                    <h3 className="font-display uppercase text-lg md:text-xl text-white tracking-tight group-hover:text-[#00FF66] transition-colors">
                                        {item.q}
                                    </h3>
                                    {open === i ? (
                                        <Minus className="w-5 h-5 text-[#00FF66] shrink-0" />
                                    ) : (
                                        <Plus className="w-5 h-5 text-white/50 shrink-0" />
                                    )}
                                </div>
                                <motion.div
                                    initial={false}
                                    animate={{
                                        height: open === i ? "auto" : 0,
                                        opacity: open === i ? 1 : 0,
                                    }}
                                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                    className="overflow-hidden"
                                >
                                    <p className="mt-4 text-white/60 leading-relaxed">
                                        {item.a}
                                    </p>
                                </motion.div>
                            </div>
                        </FadeUp>
                    ))}
                </div>
            </div>
        </section>
    );
}

function BriefingForm({ service }) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        company: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    async function onSubmit(e) {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${BACKEND}/api/leads`, {
                ...form,
                service,
                budget: "A definir",
                utm: getUtms(),
            });
            setSent(true);
            // GTM / GA4 conversion event
            if (typeof window !== "undefined") {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: "generate_lead",
                    lead_source: "landing_briefing",
                    service,
                });
                if (typeof window.gtag === "function") {
                    window.gtag("event", "generate_lead", {
                        method: "landing_briefing",
                        service,
                    });
                }
            }
            toast.success("Recebemos seu briefing! Retornaremos em 24h.");
        } catch (err) {
            toast.error("Erro ao enviar. Tente novamente.");
        } finally {
            setLoading(false);
        }
    }

    return (
        <section
            id="briefing"
            className="py-32 border-b border-white/10 bg-[#080808]"
            data-testid="briefing-section"
        >
            <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-10">
                <FadeUp className="col-span-12 lg:col-span-5">
                    <span className="font-mono-tech text-xs tracking-widest text-[#00FF66] uppercase">
                        (Vamos começar?)
                    </span>
                    <h2 className="mt-4 font-display uppercase text-5xl md:text-6xl leading-[0.95] tracking-tighter text-white">
                        Solicite<br />
                        <span className="text-white/40">sua</span>{" "}
                        <span className="text-[#00FF66]">proposta.</span>
                    </h2>
                    <p className="mt-6 text-white/60 leading-relaxed max-w-md">
                        Preencha o briefing em 2 minutos. Enviamos escopo
                        preliminar, prazo estimado e faixa de investimento em
                        até 24 horas úteis.
                    </p>
                    <div className="mt-8 space-y-3">
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() =>
                                trackEvent("contact_whatsapp", {
                                    location: "landing_briefing",
                                    service,
                                })
                            }
                            className="group flex items-center justify-between border border-white/10 p-4 hover:border-[#00FF66]/60 transition-all clip-frame-sm"
                        >
                            <div className="flex items-center gap-3">
                                <MessageCircle className="w-5 h-5 text-[#00FF66]" />
                                <span className="text-white font-display uppercase text-sm tracking-tight">
                                    WhatsApp direto
                                </span>
                            </div>
                            <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-[#00FF66]" />
                        </a>
                        <a
                            href={CALENDLY_URL}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() =>
                                trackEvent("contact_schedule", {
                                    location: "landing_briefing",
                                    service,
                                })
                            }
                            className="group flex items-center justify-between border border-white/10 p-4 hover:border-[#00FF66]/60 transition-all clip-frame-sm"
                        >
                            <div className="flex items-center gap-3">
                                <Calendar className="w-5 h-5 text-[#00FF66]" />
                                <span className="text-white font-display uppercase text-sm tracking-tight">
                                    Agendar 30 min
                                </span>
                            </div>
                            <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-[#00FF66]" />
                        </a>
                    </div>
                </FadeUp>

                <FadeUp className="col-span-12 lg:col-span-7" delay={0.1}>
                    {sent ? (
                        <div
                            className="border border-[#00FF66]/40 bg-[#00FF66]/[0.05] p-10 clip-frame-sm min-h-[400px] flex flex-col items-start justify-center"
                            data-testid="briefing-success"
                        >
                            <CheckCircle2 className="w-12 h-12 text-[#00FF66] mb-6" />
                            <h3 className="font-display uppercase text-3xl md:text-4xl text-white tracking-tight">
                                Briefing recebido.
                            </h3>
                            <p className="mt-4 text-white/70 max-w-md">
                                Nosso time analisa e retorna com escopo,
                                prazo e faixa de investimento em até 24h úteis.
                            </p>
                        </div>
                    ) : (
                        <form
                            onSubmit={onSubmit}
                            className="border border-white/10 bg-[#0a0a0a] p-8 md:p-10 clip-frame-sm space-y-5"
                            data-testid="briefing-form"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <Input
                                    label="Nome*"
                                    value={form.name}
                                    onChange={(v) => setForm({ ...form, name: v })}
                                    required
                                    testid="brief-name"
                                />
                                <Input
                                    label="E-mail*"
                                    type="email"
                                    value={form.email}
                                    onChange={(v) => setForm({ ...form, email: v })}
                                    required
                                    testid="brief-email"
                                />
                                <Input
                                    label="Telefone / WhatsApp"
                                    value={form.phone}
                                    onChange={(v) => setForm({ ...form, phone: v })}
                                    testid="brief-phone"
                                />
                                <Input
                                    label="Empresa"
                                    value={form.company}
                                    onChange={(v) => setForm({ ...form, company: v })}
                                    testid="brief-company"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-mono-tech uppercase tracking-widest text-white/50 mb-2">
                                    Descreva o projeto*
                                </label>
                                <textarea
                                    required
                                    rows={4}
                                    value={form.message}
                                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    placeholder={`Ex: preciso de um ${service.toLowerCase()} para...`}
                                    className="w-full bg-transparent border border-white/15 focus:border-[#00FF66] outline-none px-4 py-3 text-white placeholder:text-white/30 transition-colors resize-none"
                                    data-testid="brief-message"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-neon w-full justify-center disabled:opacity-60"
                                data-testid="brief-submit"
                            >
                                {loading ? "Enviando..." : "Enviar briefing"}
                                <ArrowUpRight className="w-4 h-4" />
                            </button>
                        </form>
                    )}
                </FadeUp>
            </div>
        </section>
    );
}

function Input({ label, value, onChange, type = "text", required, testid }) {
    return (
        <div>
            <label className="block text-xs font-mono-tech uppercase tracking-widest text-white/50 mb-2">
                {label}
            </label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                className="w-full bg-transparent border border-white/15 focus:border-[#00FF66] outline-none px-4 py-3 text-white transition-colors"
                data-testid={testid}
            />
        </div>
    );
}
